// Story 061 — frontend tenant resolution. The frontend reads
// window.location.hostname at startup, looks up the matching tenant entry,
// and uses its Supabase URL + anon key to talk to that tenant's data
// store. All XHR also carries X-Swiss-Tenant-Host so the shared backend
// can route to the same tenant.
//
// Cloud tenants (test/prod) draw their Supabase URL/key from build-time
// env vars (Vercel injects VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY per
// deployment). Local-dev tenants are hardcoded so `pnpm dev` and run-e2e
// work out of the box.

export type TenantSurface = 'director' | 'player';

export interface TenantConfig {
  id: string;
  surface: TenantSurface;
  supabaseUrl: string;
  supabaseAnonKey: string;
  // Story 063 — false on white-label tenants (signup also disabled at
  // the Supabase Auth layer). The frontend hides the registration form
  // and shows an "invitation only" message for a friendlier UX.
  allowsSelfRegistration: boolean;
  // Story 064 — true on white-label tenants. Drives the tournament
  // public page's host attribution branch (org profile vs. director
  // profile) and the admin shell's "Organization profile" nav item.
  isWhiteLabel: boolean;
}

const LOCAL_DEMO_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0';

function buildTenantsByHost(): Map<string, TenantConfig> {
  const m = new Map<string, TenantConfig>();

  // Local dev tenants — always present.
  const publicLocal: TenantConfig = {
    id: 'public',
    surface: 'director',
    supabaseUrl: 'http://127.0.0.1:54321',
    supabaseAnonKey: LOCAL_DEMO_ANON_KEY,
    allowsSelfRegistration: true,
    isWhiteLabel: false,
  };
  m.set('localhost', publicLocal);
  m.set('127.0.0.1', publicLocal);
  m.set('player.localhost', { ...publicLocal, surface: 'player' });
  const bgplLocalDirector = {
    id: 'bgpl',
    surface: 'director' as const,
    supabaseUrl: 'http://127.0.0.1:54331',
    supabaseAnonKey: LOCAL_DEMO_ANON_KEY,
    // White-label: directors are invited, not self-registered.
    allowsSelfRegistration: false,
    isWhiteLabel: true,
  };
  m.set('bgpl.localhost', bgplLocalDirector);
  m.set('bgpl-player.localhost', {
    ...bgplLocalDirector,
    surface: 'player',
    // Players self-register on every tenant — surface flips it on.
    allowsSelfRegistration: true,
  });

  // Cloud public tenant — present when VITE_SUPABASE_URL is set (test/
  // prod builds). Vercel injects per-deployment env at build time.
  const cloudUrl = import.meta.env.VITE_SUPABASE_URL;
  const cloudKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (cloudUrl && cloudKey) {
    const cloudPublic: TenantConfig = {
      id: 'public',
      surface: 'director',
      supabaseUrl: cloudUrl,
      supabaseAnonKey: cloudKey,
      allowsSelfRegistration: true,
      isWhiteLabel: false,
    };
    m.set('swisstest.backgammon.pl', cloudPublic);
    m.set('playerstest.backgammon.pl', {
      ...cloudPublic,
      surface: 'player',
    });
  }

  // Cloud bgpl (white-label) tenant. Each environment (test, prod)
  // gets its own Vercel project that sets the hostname pair via
  // VITE_TENANT_BGPL_DIRECTOR_HOSTNAME + VITE_TENANT_BGPL_PLAYER_HOSTNAME
  // at build time. The hostnames were hardcoded here previously — both
  // the test pair (bgswisstest / bgplayerstest) and the prod pair
  // (swiss / players) lived in the same bundle, which broke
  // playerHostForCurrentTenant() on prod (it returned the test
  // hostname because both matched the supabaseUrl filter).
  //
  // Driving from env vars keeps each build's tenants map env-pure:
  // prod knows only swiss/players, test knows only bgswisstest/
  // bgplayerstest, local dev knows neither (use the bgpl.localhost /
  // bgpl-player.localhost entries seeded above).
  const bgplUrl = import.meta.env.VITE_TENANT_BGPL_SUPABASE_URL;
  const bgplKey = import.meta.env.VITE_TENANT_BGPL_SUPABASE_ANON_KEY;
  const bgplDirectorHost = import.meta.env.VITE_TENANT_BGPL_DIRECTOR_HOSTNAME;
  const bgplPlayerHost = import.meta.env.VITE_TENANT_BGPL_PLAYER_HOSTNAME;
  if (bgplUrl && bgplKey && bgplDirectorHost && bgplPlayerHost) {
    const bgplBase: Omit<TenantConfig, 'surface' | 'allowsSelfRegistration'> = {
      id: 'bgpl',
      supabaseUrl: bgplUrl,
      supabaseAnonKey: bgplKey,
      isWhiteLabel: true,
    };
    m.set(bgplDirectorHost, {
      ...bgplBase,
      surface: 'director' as const,
      allowsSelfRegistration: false,
    });
    m.set(bgplPlayerHost, {
      ...bgplBase,
      surface: 'player' as const,
      allowsSelfRegistration: true,
    });
  }

  return m;
}

const tenantsByHost = buildTenantsByHost();

export function resolveTenant(hostname: string): TenantConfig | null {
  return tenantsByHost.get(hostname.toLowerCase()) ?? null;
}

export function currentTenantOrNull(): TenantConfig | null {
  if (typeof window === 'undefined') return null;
  return resolveTenant(window.location.hostname);
}

// Find the player-surface host for the same tenant as the current page.
// Used to build share links that cross from director surface to player
// surface within the same tenant. Returns null when the current tenant
// doesn't have a configured player host.
//
// Match on supabaseUrl in addition to tenant id, so that on a deployed
// build (which has BOTH local + cloud entries for the same id) we pick
// the cloud player host, not player.localhost. Same id alone isn't
// enough to disambiguate.
export function playerHostForCurrentTenant(): string | null {
  const current = currentTenantOrNull();
  if (!current) return null;
  for (const [host, cfg] of tenantsByHost) {
    if (
      cfg.id === current.id &&
      cfg.surface === 'player' &&
      cfg.supabaseUrl === current.supabaseUrl
    ) {
      return host;
    }
  }
  return null;
}
